import { useState } from 'react';
import { z } from 'zod';
import type { InviteUserCommand, UpdateProjectCommand } from '@/api/generated';
import { useContacts, useCreateShareLink, useInviteUser, useUpdateProject } from '@/api/hooks';
import { Btn } from '@/design-system/primitives/Btn/Btn';
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/design-system/primitives/Dialog/Dialog';
import { Form, FormField, FormSubmitButton } from '@/design-system/primitives/Form/Form';
import { Input } from '@/design-system/primitives/Input/Input';
import { toast } from '@/design-system/primitives/Toast/Toast';

// ---------- Project field edit ----------

/**
 * Project field key the dialog can edit. The backend's `UpdateProjectCommand`
 * accepts name / address / startDate / endDate / budgetTotal / currentPhaseId /
 * projectType (BF-11) plus taskNotificationDefaultOffsetsDays /
 * legalNotificationContactId (notification config). The fields below are the
 * ones exposed in the UI; the wizard handles `currentPhaseId` and `projectType`
 * on its own.
 */
export type ProjectFieldKey =
  | 'name'
  | 'address'
  | 'startDate'
  | 'endDate'
  | 'budgetTotal'
  | 'taskNotificationOffsets'
  | 'legalNotificationContact';

const PROJECT_FIELD_META: Record<ProjectFieldKey, { label: string; placeholder: string }> = {
  name: { label: 'Projectnaam', placeholder: "bv. 'Nieuwbouw Bertem'" },
  address: { label: 'Adres', placeholder: 'Straat, postcode gemeente' },
  startDate: { label: 'Startdatum', placeholder: '' },
  endDate: { label: 'Verwachte oplevering', placeholder: '' },
  budgetTotal: { label: 'Budget (totaal)', placeholder: '520000' },
  taskNotificationOffsets: {
    label: 'Notificaties standaard (dagen voor deadline)',
    placeholder: 'bv. 7, 1',
  },
  legalNotificationContact: {
    label: 'Verantwoordelijke wettelijke notificaties',
    placeholder: '',
  },
};

const projectFieldSchema = z.object({
  value: z.string().trim().max(200, 'Maximaal 200 tekens'),
});

type ProjectFieldFormValues = z.infer<typeof projectFieldSchema>;

/**
 * Parse a comma-separated day list (e.g. "7, 1") into a clean number[].
 * Drops empty segments and anything that isn't a finite, non-negative number.
 */
function parseOffsets(input: string): number[] {
  return input
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
    .map(Number)
    .filter((n) => Number.isFinite(n) && n >= 0);
}

export function ProjectFieldDialog({
  open,
  onOpenChange,
  field,
  initialValue,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  field: ProjectFieldKey;
  initialValue: string;
}) {
  const meta = PROJECT_FIELD_META[field];
  const mutation = useUpdateProject();
  const contacts = useContacts({ pageSize: 100 }).data?.items ?? [];
  const inputType = field === 'startDate' || field === 'endDate' ? 'date' : 'text';
  const inputMode = field === 'budgetTotal' ? 'decimal' : undefined;
  const isOffsets = field === 'taskNotificationOffsets';
  const isLegalContact = field === 'legalNotificationContact';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle>{meta.label} bewerken</DialogTitle>
        <DialogDescription>Wijzigingen zijn meteen zichtbaar in het dashboard.</DialogDescription>
        <DialogBody>
          <Form<ProjectFieldFormValues>
            schema={projectFieldSchema}
            defaultValues={{ value: initialValue }}
            onSubmit={async ({ value }) => {
              // Build a partial command: only set the field we're editing, leave
              // the rest null so the backend doesn't wipe them. UpdateProjectCommand
              // is "merge nullable" — null means "no change", non-null means "set".
              const body: UpdateProjectCommand = {
                name: field === 'name' ? value.trim() : null,
                address: field === 'address' ? value.trim() || null : null,
                startDate: field === 'startDate' ? value || null : null,
                endDate: field === 'endDate' ? value || null : null,
                budgetTotal:
                  field === 'budgetTotal' ? value.replace(',', '.').trim() || null : null,
                currentPhaseId: null,
                projectType: null,
                // Comma-separated days → number[]; empty list still sends [] so
                // the user can deliberately clear all default offsets.
                taskNotificationDefaultOffsetsDays: isOffsets ? parseOffsets(value) : null,
                // '' in the dropdown means "no responsible contact" → null.
                legalNotificationContactId: isLegalContact ? value || null : null,
              };
              await mutation.mutateAsync(body);
              onOpenChange(false);
            }}
          >
            <FormField<ProjectFieldFormValues>
              name="value"
              label={meta.label}
              required={!isLegalContact}
            >
              {(f) =>
                isLegalContact ? (
                  <select
                    id={f.id}
                    ref={f.ref}
                    name={f.name}
                    value={(f.value as string | undefined) ?? ''}
                    onChange={f.onChange}
                    onBlur={f.onBlur}
                  >
                    <option value="">— Geen —</option>
                    {contacts.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name} — {c.role}
                      </option>
                    ))}
                  </select>
                ) : (
                  <Input
                    {...f}
                    value={f.value ?? ''}
                    type={inputType}
                    inputMode={inputMode}
                    placeholder={meta.placeholder}
                    autoFocus
                  />
                )
              }
            </FormField>
            <DialogFooter>
              <DialogClose asChild>
                <Btn type="button">Annuleer</Btn>
              </DialogClose>
              <FormSubmitButton>Opslaan</FormSubmitButton>
            </DialogFooter>
          </Form>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Invite user ----------

const inviteSchema = z.object({
  email: z.string().trim().email('Geen geldig e-mailadres'),
});

type InviteFormValues = z.infer<typeof inviteSchema>;

export function InviteUserDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const mutation = useInviteUser();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle>Persoon uitnodigen</DialogTitle>
        <DialogDescription>
          Stuur een uitnodiging per e-mail. De ontvanger krijgt een magic-link om in te loggen.
        </DialogDescription>
        <DialogBody>
          <Form<InviteFormValues>
            schema={inviteSchema}
            defaultValues={{ email: '' }}
            onSubmit={async (values) => {
              const body: InviteUserCommand = { email: values.email.trim() };
              await mutation.mutateAsync(body);
              onOpenChange(false);
            }}
          >
            <FormField<InviteFormValues> name="email" label="E-mailadres" required>
              {(f) => (
                <Input
                  {...f}
                  value={f.value ?? ''}
                  type="email"
                  placeholder="naam@bedrijf.be"
                  autoFocus
                />
              )}
            </FormField>
            <DialogFooter>
              <DialogClose asChild>
                <Btn type="button">Annuleer</Btn>
              </DialogClose>
              <FormSubmitButton>Verstuur uitnodiging</FormSubmitButton>
            </DialogFooter>
          </Form>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Share-link create + display ----------

/**
 * "Read-only deellink maken" surfaces the just-created URL. We render the
 * result inline in a small dialog with a copy-to-clipboard control so the
 * user can hand the URL off in a single gesture.
 */
export function ShareLinkDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const mutation = useCreateShareLink();
  const [createdUrl, setCreatedUrl] = useState<string | null>(null);

  // The 201 response body shape isn't strongly typed. Pluck whatever URL-ish
  // field came back — backend test stub returns { id, url, expiresAt }.
  function extractUrl(raw: unknown): string | null {
    if (typeof raw === 'string') return raw;
    if (raw && typeof raw === 'object') {
      const obj = raw as Record<string, unknown>;
      if (typeof obj.url === 'string') return obj.url;
      if (typeof obj.id === 'string') return `/share/${obj.id}`;
    }
    return null;
  }

  const handleCreate = async () => {
    const data = await mutation.mutateAsync();
    const url = extractUrl(data);
    if (url) {
      setCreatedUrl(url);
    } else {
      toast.error('Geen URL teruggekregen.');
    }
  };

  const copyToClipboard = async () => {
    if (!createdUrl) return;
    try {
      await navigator.clipboard.writeText(createdUrl);
      toast.success('Link gekopieerd.');
    } catch {
      toast.error('Kopiëren mislukt — kopieer handmatig.');
    }
  };

  // Reset transient state whenever the dialog closes so a re-open starts
  // clean. (Setting state during open=>false is safe; it just preps next mount.)
  const handleOpenChange = (next: boolean) => {
    if (!next) setCreatedUrl(null);
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogTitle>Read-only deellink</DialogTitle>
        <DialogDescription>
          De link geeft alleen leesrechten en vervalt automatisch na 30 dagen.
        </DialogDescription>
        <DialogBody>
          {createdUrl === null ? (
            <p>Klik "Aanmaken" om een nieuwe deellink te genereren.</p>
          ) : (
            <div className="col gap-8">
              <label htmlFor="share-link-url" className="eyebrow">
                Deel deze URL
              </label>
              <Input
                id="share-link-url"
                readOnly
                value={createdUrl}
                onFocus={(e) => e.currentTarget.select()}
                className="mono"
              />
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          {createdUrl === null ? (
            <>
              <DialogClose asChild>
                <Btn type="button">Annuleer</Btn>
              </DialogClose>
              <Btn
                type="button"
                variant="primary"
                onClick={handleCreate}
                disabled={mutation.isPending}
              >
                {mutation.isPending ? 'Bezig…' : 'Aanmaken'}
              </Btn>
            </>
          ) : (
            <>
              <Btn type="button" onClick={copyToClipboard}>
                Kopieer link
              </Btn>
              <DialogClose asChild>
                <Btn type="button" variant="primary">
                  Sluiten
                </Btn>
              </DialogClose>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
